/* eslint-disable no-return-await */
import { makeObservable, action, observable, override, runInAction } from 'mobx';
import { CommonService } from './common';
import common from '@/utils/v2/common';
import Home from './api.interface';
import { HomeModel } from './home.model';
import { DisburseSummaryModel } from './disburseSummary.model';
import { UC_TOGGLE_STATUS } from '@/stores/userInfo/userInfo.interface';
import { sendUserAction, UserActions } from '@/utils/v2/idap';

// 应产品要求，将阶梯费率格式化，保证至少两位小数
const formatUserTieredFee = (userInfo: HomeModel) => {
  userInfo?.user_tiered_fee_info?.list?.forEach((item) => {
    item.fee = common.atLest2Decimals(item.fee);
  });
  if (userInfo?.user_tiered_fee_info?.current_daily_fee) {
    userInfo.user_tiered_fee_info.current_daily_fee = common.atLest2Decimals(
      userInfo.user_tiered_fee_info.current_daily_fee,
    );
  }
  return userInfo;
};

export class HomeService extends CommonService {
  /** ID 首页放款摘要(独立 model)，数据来源 query_disburse_summary */
  @observable disburseSummary: DisburseSummaryModel = new DisburseSummaryModel();

  constructor() {
    super();
    makeObservable(this);
  }

  @override
  async getHomeInfo(): Promise<HomeModel> {
    const { code, result } = await this.post(Home.RefactorAPI.get_homepage_info_new);
    if (code === 0) {
      const formatedResult = formatUserTieredFee(result);
      runInAction(() => {
        this.home = {
          ...this.home,
          ...formatedResult,
        };
      });
    }
    return this.home;
  }

  @action
  async getDisburseSummary() {
    const { code, result } = await this.post(Home.RefactorAPI.query_disburse_summary);
    if (code !== 0) {
      return false;
    }
    runInAction(() => {
      this.disburseSummary = {
        loan1_disburse_amount: result?.loan1_disburse_amount ?? '0',
        loan2_disburse_amount: result?.loan2_disburse_amount ?? '0',
        to_be_received_amount: result?.to_be_received_amount ?? '0',
        loan1_disburse_time: result?.loan1_disburse_time ?? 0,
        loan2_disburse_time: result?.loan2_disburse_time ?? 0,
      } as DisburseSummaryModel;
    });
    return true;
  }

  @action
  async saveHomeInfo(params: HomeModel) {
    this.home = {
      ...this.home,
      ...params,
    };
  }

  @action
  async getLimitAdjustInfo() {
    const { code, result } = await this.post(Home.API.get_limit_adjust_info);
    if (code === 0) {
      const {
        limit_is_change,
        limit_change_time,
        from_id,
        from_limit,
        to_id,
        to_limit,
        temp_limit,
      } = result;

      runInAction(() => {
        this.home = {
          ...this.home,
          onboardingLimitAdjust: {
            limit_is_change,
            limit_change_time,
            from_id,
            from_limit,
            to_id,
            to_limit,
          },
          temporaryLimitAdjust: temp_limit?.[0] ?? {},
        };
      });
    }
  }

  @action
  async markLimitAdjust(params: { from_id?: number; to_id?: number; limit_type?: number }) {
    return await this.post(Home.API.commit_limit_change, {
      body: params,
    });
  }

  @override
  async getLatestDisbursedGroups() {
    return await this.post(Home.RefactorAPI.get_user_latest_disbursed_groups);
  }
  @override
  async updateUserConfig(params: {
    toggle_status?: UC_TOGGLE_STATUS;
    disburse_percentage?: number | string;
  }) {
    if (params?.toggle_status === UC_TOGGLE_STATUS.ENABLED) {
      sendUserAction(UserActions.Home.ClickEnable);
    }
    if (params?.toggle_status === UC_TOGGLE_STATUS.DISABLED) {
      sendUserAction(UserActions.Home.ClickDisable);
    }
    return await this.post(Home.RefactorAPI.update_user_config, {
      body: params,
      manual: true,
    });
  }

  /**
   *
   * 单独更新设置的比例，也可以请求方式getHomeInfo来更新
   * @param {number} [percentage=0]
   * @memberof HomeService
   */
  @override
  async updatePercentage(percentage: number) {
    this.home = {
      ...this.home,
      schedule_disburse_percentage: percentage,
    };
  }
}
